import type { ReactNode } from 'react';
import { lazyWithRetry as lazy } from '../lib/lazyWithRetry';
import type { TabInstance } from '../hooks/useTabs';
import type { MenuColumn, MenuItem } from '../components/MegaMenu';
import { ComingSoonPage } from '../pages/ComingSoonPage';
import { formatDate } from '../lib/datetime';

// Kodeopdeling pr. side (0.37.0): hver fane-side hentes først, når fanen åbnes.
const MapOverviewPage = lazy(() => import('../pages/MapOverviewPage').then((m) => ({ default: m.MapOverviewPage })));
const PlanEditorPage = lazy(() => import('../pages/PlanEditorPage').then((m) => ({ default: m.PlanEditorPage })));
const PlanDayPage = lazy(() => import('../pages/PlanDayPage').then((m) => ({ default: m.PlanDayPage })));
const CitizensPage = lazy(() => import('../pages/CitizensPage').then((m) => ({ default: m.CitizensPage })));
const InstitutionsPage = lazy(() => import('../pages/InstitutionsPage').then((m) => ({ default: m.InstitutionsPage })));
const ContractsPage = lazy(() => import('../pages/ContractsPage').then((m) => ({ default: m.ContractsPage })));
const SystemPage = lazy(() => import('../pages/SystemPage').then((m) => ({ default: m.SystemPage })));
const CalendarsPage = lazy(() => import('../pages/CalendarsPage').then((m) => ({ default: m.CalendarsPage })));
const TripsAdminPage = lazy(() => import('../pages/TripsAdminPage').then((m) => ({ default: m.TripsAdminPage })));
const TripStatusPage = lazy(() => import('../pages/TripStatusPage').then((m) => ({ default: m.TripStatusPage })));
const FleetPage = lazy(() => import('../pages/mock/FleetPage').then((m) => ({ default: m.FleetPage })));
const TicketsPage = lazy(() => import('../pages/TicketsPage').then((m) => ({ default: m.TicketsPage })));
const VehiclesPage = lazy(() => import('../pages/VehiclesPage').then((m) => ({ default: m.VehiclesPage })));
const CancellationTypesPage = lazy(() => import('../pages/CancellationTypesPage').then((m) => ({ default: m.CancellationTypesPage })));
const PlansPage = lazy(() => import('../pages/PlansPage').then((m) => ({ default: m.PlansPage })));
const TrackingPage = lazy(() => import('../pages/mock/TrackingPage').then((m) => ({ default: m.TrackingPage })));

/**
 * Fanetyper. En fane er en instans af en af disse: "kort" findes altid én gang,
 * mens "plan-dag" kan vaere aabnet flere gange med forskellige datoer.
 */
export type TabKind =
  | 'kort'
  | 'planredigering'
  | 'plan-dag'
  | 'planer'
  | 'ture'
  | 'turstatus'
  | 'borgere'
  | 'institutioner'
  | 'kontrakter'
  | 'flaade'
  | 'tracking'
  | 'sager'
  | 'kalendere'
  | 'vogne'
  | 'afbestillingstyper'
  | 'system'
  | 'tickets';

/**
 * Standardfanen. Den kan lukkes som alle andre, men aabnes automatisk igen,
 * naar den sidste fane lukkes (se useTabs' fallbackTab).
 */
export const HOME_TAB: TabInstance = {
  id: 'kort',
  kind: 'kort',
  label: 'Kort oversigt',
  path: '/',
  closable: true,
};

interface KindDef {
  label: string;
  path: string;
  /** Kommende side: vises som "kommer i fase N". */
  phase?: number;
}

const KINDS: Record<Exclude<TabKind, 'plan-dag'>, KindDef> = {
  kort: { label: 'Kort oversigt', path: '/' },
  planredigering: { label: 'Planredigering', path: '/planredigering' },
  planer: { label: 'Administrer planer', path: '/planer' },
  ture: { label: 'Administrer ture', path: '/ture' },
  turstatus: { label: 'Tur status', path: '/tur-status' },
  borgere: { label: 'Borger søgning', path: '/borgere' },
  institutioner: { label: 'Institutioner', path: '/institutioner' },
  kontrakter: { label: 'Kontrakter', path: '/kontrakter' },
  flaade: { label: 'Flåde', path: '/flaade', phase: 3 },
  tracking: { label: 'Tracking', path: '/tracking', phase: 4 },
  sager: { label: 'Sager', path: '/sager', phase: 3 },
  kalendere: { label: 'Kalendere', path: '/kalendere' },
  vogne: { label: 'Vogne', path: '/vogne' },
  afbestillingstyper: { label: 'Afbestillingstyper', path: '/afbestillingstyper' },
  system: { label: 'System', path: '/system' },
  tickets: { label: 'Tickets', path: '/tickets' },
};

/** Den fjernede side Fast kørsel; se tabFromPath. */
const LEGACY_SUBSCRIPTIONS_PATH = '/fast-koersel';

/** Menuens kolonner. Rækkefølgen er den, brugeren ser i mega-dropdownen. */
export const MENU_COLUMNS: MenuColumn[] = [
  {
    title: 'Planlægning',
    items: [
      { kind: 'kort', label: 'Kort oversigt' },
      { kind: 'planredigering', label: 'Planredigering' },
      { kind: 'planer', label: 'Administrer planer' },
    ],
  },
  {
    title: 'Kørsler',
    items: [
      { kind: 'ture', label: 'Administrer ture' },
      { kind: 'turstatus', label: 'Tur status' },
    ],
  },
  { title: 'Borgere', items: [{ kind: 'borgere', label: 'Borger søgning' }] },
  { title: 'Institutioner', items: [{ kind: 'institutioner', label: 'Institutioner' }] },
  { title: 'Kontrakter', items: [{ kind: 'kontrakter', label: 'Kontrakter' }] },
  { title: 'Flåde', items: [{ kind: 'flaade', label: 'Flåde' }] },
  { title: 'Tracking', items: [{ kind: 'tracking', label: 'Tracking' }] },
  { title: 'Sager', items: [{ kind: 'sager', label: 'Sager' }] },
  {
    title: 'Administration',
    items: [
      { kind: 'kalendere', label: 'Kalendere' },
      { kind: 'vogne', label: 'Vogne' },
      { kind: 'afbestillingstyper', label: 'Afbestillingstyper' },
      { kind: 'system', label: 'System' },
      { kind: 'tickets', label: 'Tickets' },
    ],
  },
];

/** Kender fanetypen? Gemte faner af en fjernet type (Scheduling, Dispatch) genåbnes ikke. */
export function isKnownTabKind(kind: string): boolean {
  return kind === 'plan-dag' || kind in KINDS;
}

/** Bygger fane-instansen for et menupunkt eller en dyb URL. */
export function createTab(kind: TabKind, params: Record<string, string> = {}): TabInstance {
  if (kind === 'plan-dag') {
    const date = params.date ?? new Date().toISOString().slice(0, 10);
    return {
      // Id'et indeholder datoen, saa den samme dag ikke aabnes to gange, men to
      // forskellige datoer kan vaere aabne samtidig.
      id: `plan-dag:${date}`,
      kind,
      label: `Plan: ${formatDate(date)}`,
      params: { date },
      path: `/plan/${date}`,
      closable: true,
    };
  }
  const def = KINDS[kind];
  const query = new URLSearchParams(params).toString();
  return {
    id: kind,
    kind,
    label: def.label,
    params,
    path: query ? `${def.path}?${query}` : def.path,
    closable: true,
  };
}

export function tabFromMenuItem(item: MenuItem): TabInstance {
  return createTab(item.kind as TabKind, item.params ?? {});
}

/** URL -> fane, saa et dybt link (eller et browser-genindlæs) rammer rigtigt. */
export function tabFromPath(pathname: string, search = ''): TabInstance | null {
  const params = Object.fromEntries(new URLSearchParams(search));
  const planMatch = /^\/plan\/(\d{4}-\d{2}-\d{2})$/.exec(pathname);
  if (planMatch) return createTab('plan-dag', { date: planMatch[1] });

  // Fast kørsel er ikke længere en selvstændig side (ticket-3): gamle bogmærker
  // til /fast-koersel sendes videre til Administrer ture, med id'et hvis det er med.
  if (pathname === LEGACY_SUBSCRIPTIONS_PATH) {
    return createTab('ture', params.id ? { subscriptionId: params.id } : {});
  }

  const entry = (Object.entries(KINDS) as [Exclude<TabKind, 'plan-dag'>, KindDef][]).find(
    ([, def]) => def.path === pathname,
  );
  if (!entry) return null;
  return createTab(entry[0], params);
}

export interface TabActions {
  openTab: (tab: TabInstance) => void;
  /** Label paa den senest aktive fane foer Tickets (forudfylder Område). */
  getArea?: () => string | undefined;
}

/** Indholdet i en fane. Alle aabne faner renderes; kun den aktive er synlig. */
export function renderTabContent(tab: TabInstance, actions: TabActions): ReactNode {
  switch (tab.kind as TabKind) {
    case 'kort':
      return <MapOverviewPage onPlan={() => actions.openTab(createTab('planredigering'))} />;
    case 'planredigering':
      return (
        <PlanEditorPage onOpenDay={(date) => actions.openTab(createTab('plan-dag', { date }))} />
      );
    case 'plan-dag':
      return <PlanDayPage date={tab.params?.date ?? ''} instanceId={tab.id} />;
    case 'borgere':
      return (
        <CitizensPage
          onOpenTrips={(id) => actions.openTab(createTab('ture', { citizenId: id }))}
        />
      );
    case 'institutioner':
      return (
        <InstitutionsPage
          onOpenCalendars={(calendarId) =>
            actions.openTab(createTab('kalendere', calendarId ? { id: calendarId } : {}))
          }
        />
      );
    case 'kontrakter':
      return <ContractsPage />;
    case 'kalendere':
      return <CalendarsPage calendarId={tab.params?.id} />;
    case 'ture':
      return (
        <TripsAdminPage
          citizenId={tab.params?.citizenId}
          subscriptionId={tab.params?.subscriptionId}
          onOpenCitizen={(id) => actions.openTab(createTab('borgere', { id }))}
        />
      );
    case 'turstatus':
      return (
        <TripStatusPage
          onOpenSubscription={(id) => actions.openTab(createTab('ture', { subscriptionId: id }))}
        />
      );
    case 'system':
      return <SystemPage />;
    case 'vogne':
      return <VehiclesPage />;
    case 'afbestillingstyper':
      return <CancellationTypesPage />;
    case 'flaade':
      return <FleetPage />;
    case 'tickets':
      return <TicketsPage getArea={actions.getArea} />;
    case 'planer':
      return (
        <PlansPage
          onOpenPlan={(date) => actions.openTab(createTab('plan-dag', { date }))}
          onOpenSubscription={(id) => actions.openTab(createTab('ture', { subscriptionId: id }))}
        />
      );
    case 'tracking':
      return <TrackingPage />;
    default: {
      const def = KINDS[tab.kind as Exclude<TabKind, 'plan-dag'>];
      return <ComingSoonPage title={def?.label ?? tab.label} phase={def?.phase ?? 1} />;
    }
  }
}
